import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Col } from "react-bootstrap";
import { useFormik } from "formik";
import { post, put, get, updateToken } from "../../../../../helpers/api_helper";
import add_stock from "../../../../../Assets/svg/stockExchange/add_stock.svg";
import toast from "react-hot-toast";
import { PLANET_NAMES } from "../../../../../helpers/constants";
import { useSelector } from "react-redux";
import Select from "react-select";

const AddNewSecondaryPlanet = ({
  showModal,
  setShowModal,
  handleDataRefresh,
  mode,
  secondaryPlanetData,
  resetFormOnClose,
}) => {
  const token = useSelector((state) => state.login.token);
  const [masterPlanets, setMasterPlanets] = useState([]);

  useEffect(() => {
    if (token) {
      updateToken(token);
    }

    const fetchMasterPlanets = async () => {
      try {
        const response = await get("/planet/master");
        if (response) {
          setMasterPlanets(response?.data?.items);
        }
      } catch (error) {
        console.error("Error fetching master planets:", error);
      }
    };

    fetchMasterPlanets();
  }, [token]);

  const planetOptions = Object.keys(PLANET_NAMES).map((key) => ({
    value: key,
    label: PLANET_NAMES[key],
  }));

  const formik = useFormik({
    initialValues: {
      name: [],
      master_planet: "",
      status: "active",
      master: false,
    },
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      let names = values.name.map((option) => option.value).toString();
      const updatedValues = {
        ...values,
        name: names,
      };
      try {
        if (mode === "create") {
          await post("/planet", updatedValues);
          formik.resetForm();
          toast.success("Secondary planet is created successfully");
        } else if (mode === "edit") {
          await put(`/planet/${secondaryPlanetData._id}`, {
            ...updatedValues,
            master: false,
          });
          toast.success("Secondary planet is updated successfully");
        }
        setShowModal(false);
        handleDataRefresh();
      } catch (error) {
        console.error("Error submitting secondary planet:", error);
        setErrors({
          submissionError: error.response?.data?.message
            ? error.response?.data?.message
            : error.response?.data?.error || "An error occurred.",
        });
        toast.error(
          error.response?.data?.message
            ? error.response?.data?.message
            : error.response?.data?.error || "An error occurred."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (mode === "edit" && secondaryPlanetData) {
      const namesArray = secondaryPlanetData.name.split(",").map((name) => ({
        value: name,
        label: PLANET_NAMES[name] || name,
      }));

      formik.setValues({
        name: namesArray,
        master_planet: secondaryPlanetData.master_planet,
        status: secondaryPlanetData.status,
        master: secondaryPlanetData.master,
      });
    } else if (resetFormOnClose) {
      formik.resetForm();
    }
  }, [mode, secondaryPlanetData, resetFormOnClose]);

  return (
    <Modal
      show={showModal}
      size="md"
      onHide={() => setShowModal(false)}
      backdrop="static"
      keyboard={false}
      centered
      scrollable
    >
      <Modal.Body>
        <div className="topHeader d-flex align-items-start justify-content-between gap-10 pb-2">
          <div className="left">
            <h5 className="m-0 fw-sbold">
              {mode === "create"
                ? "Add New Secondary Planet"
                : "Edit Secondary Planet"}
            </h5>
            <p className="m-0 text-muted">
              {mode === "create"
                ? "Add a new secondary planet to the list."
                : "Edit the details of the secondary planet."}
            </p>
          </div>
          <Button
            className="p-0 border-0"
            variant="transparent"
            onClick={() => setShowModal(false)}
          >
            <img src={add_stock} alt="" />
          </Button>
        </div>
        <div className="FormWrapper">
          {formik.errors.submissionError && (
            <div className="alert alert-danger">
              {formik.errors.submissionError}
            </div>
          )}
          <Form onSubmit={formik.handleSubmit}>
            <Col className="my-2" lg="12">
              <label htmlFor="name" className="form-label text-dark m-0 pb-1">
                Name
              </label>
              <Select
                id="name"
                name="name"
                value={formik.values.name}
                options={planetOptions}
                onChange={(selected) =>
                  formik.setFieldValue("name", selected ? selected : [])
                }
                isClearable={true}
                isDisabled={formik.isSubmitting}
                maxMenuHeight={150}
                isMulti={true}
              />
              {formik.touched.name && formik.errors.name ? (
                <div className="text-danger">{formik.errors.name}</div>
              ) : null}
            </Col>

            <Col className="my-2" lg="12">
              <label
                htmlFor="master_planet"
                className="form-label text-dark m-0 pb-1"
              >
                Master Planet
              </label>
              <Form.Select
                id="master_planet"
                name="master_planet"
                value={
                  formik.values.master_planet
                    ? formik.values.master_planet._id
                    : ""
                }
                onChange={(e) => {
                  const selectedPlanet = masterPlanets.find(
                    (planet) => planet._id === e.target.value
                  );
                  formik.setFieldValue("master_planet", selectedPlanet);
                }}
              >
                <option value="">Select Master Planet</option>
                {masterPlanets.map((planet) => (
                  <option key={planet?._id} value={planet?._id}>
                    {`${planet?.name} (${planet?.stock?.ticker_name}) - (${planet?.stock?.stock_name})`}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col lg="12" className="my-2">
              <div className="d-flex align-items-center justify-content-between gap-10">
                <label
                  htmlFor="status"
                  className="form-label text-dark fw-sbold m-0"
                >
                  Status
                </label>
                <Form.Check
                  type="switch"
                  id="status"
                  name="status"
                  checked={formik.values.status === "active"}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "status",
                      e.target.checked ? "active" : "inactive"
                    )
                  }
                />
              </div>
            </Col>

            <Col className="my-2" lg="12">
              <Button
                type="submit"
                className="d-flex align-items-center w-100 justify-content-center commonBtn"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span className="ms-2">Saving...</span>
                  </>
                ) : mode === "create" ? (
                  "Add Secondary Planet"
                ) : (
                  "Update Secondary Planet"
                )}
              </Button>
            </Col>
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddNewSecondaryPlanet;
